import { useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import regex from 'regex'
import strings from 'strings'

export interface IEmailChipsFormFieldProps {
  id?: string
  name: string
  label?: string
  placeholder?: string
  disabled?: boolean
  maxEmails?: number
  maxLength?: number
  helperText?: string
}

const DEFAULT_MAX_EMAILS = 10
const DEFAULT_MAX_LENGTH = 100
const SEPARATOR_KEYS = [' ']

export const isValidEmail = (email: string): boolean =>
  regex.EMAIL_REGEX_PATTERN.test(email.trim())

export const splitPastedEmails = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)

const EmailChipsFormField: React.FC<IEmailChipsFormFieldProps> = (props) => {
  const {
    id,
    name,
    label,
    placeholder,
    disabled = false,
    maxEmails = DEFAULT_MAX_EMAILS,
    maxLength = DEFAULT_MAX_LENGTH,
    helperText,
  } = props
  const { control } = useFormContext()
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const rejectionReason = (current: string[], email: string): string | null => {
    if (email.length > maxLength) {
      return `Max length allowed is ${maxLength}`
    }
    if (!isValidEmail(email)) {
      return strings.PLEASE_ENTER_A_VALID_EMAIL
    }
    if (current.some((existing) => existing.toLowerCase() === email.toLowerCase())) {
      return strings.EMAIL_ALREADY_ADDED
    }
    if (current.length >= maxEmails) {
      return `Max ${maxEmails} emails allowed`
    }
    return null
  }

  const addEntries = (
    current: string[],
    candidates: string[]
  ): { next: string[]; error: string | null; rejected: string | null } => {
    const next = [...current]
    let failure: string | null = null
    let rejected: string | null = null

    candidates.forEach((candidate) => {
      const email = candidate.trim()
      if (!email) {
        return
      }
      const reason = rejectionReason(next, email)
      if (reason) {
        if (!failure) {
          failure = reason
          rejected = email
        }
        return
      }
      next.push(email)
    })

    return { next, error: failure, rejected }
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field: { onChange, value } }) => {
        const emails: string[] = Array.isArray(value) ? value : []

        const commit = (candidates: string[]) => {
          const { next, error: failure, rejected } = addEntries(emails, candidates)
          if (next.length !== emails.length) {
            onChange(next)
          }
          setError(failure)
          setInputValue(rejected || '')
        }

        return (
          <Autocomplete
            id={id}
            multiple
            freeSolo
            disabled={disabled}
            options={[]}
            value={emails}
            inputValue={inputValue}
            onInputChange={(_event, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue)
                setError(null)
              }
            }}
            onChange={(_event, newValue, reason) => {
              if (reason === 'removeOption' || reason === 'clear') {
                onChange(newValue as string[])
                setError(null)
                return
              }
              const candidates = (newValue as string[]).filter(
                (candidate) => !emails.includes(candidate)
              )
              commit(candidates)
            }}
            renderTags={(tags: string[], getTagProps) =>
              tags.map((email: string, index: number) => (
                <Chip
                  variant="outlined"
                  size="small"
                  label={email}
                  {...getTagProps({ index })}
                  key={email}
                  data-testid={`${name}-chip-${email}`}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                margin="normal"
                fullWidth
                placeholder={emails.length === 0 ? placeholder : ''}
                error={Boolean(error)}
                helperText={error || (inputValue.trim() ? helperText : undefined)}
                inputProps={{
                  ...params.inputProps,
                  'data-testid': `${name}-input`,
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (SEPARATOR_KEYS.includes(event.key)) {
                      if (!inputValue.trim()) {
                        return
                      }
                      event.preventDefault()
                      event.stopPropagation()
                      commit([inputValue])
                      return
                    }
                    ;(params.inputProps as any)?.onKeyDown?.(event)
                  },
                  onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                    if (inputValue.trim()) {
                      commit([inputValue])
                    }
                    ;(params.inputProps as any)?.onBlur?.(event)
                  },
                  onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
                    const candidates = splitPastedEmails(
                      event.clipboardData.getData('text') || ''
                    )
                    if (candidates.length > 1) {
                      event.preventDefault()
                      commit(candidates)
                    }
                  },
                }}
              />
            )}
          />
        )
      }}
    />
  )
}

export default EmailChipsFormField
